import { useState } from 'react';

import { FinalRecommendationOption, FinalRecommendationQuestionProps } from './types';
import { Box, ToggleButton, ToggleButtonGroup, useTheme } from '@mui/material';
import { QuestionLabel } from './QuestionLabel';
import { FinalRecommendation } from 'utils/typedEnums';
import {
  CandidateFeedbackFinalRecommendation,
  LongCandidateFeedbackFinalRecommendation,
} from 'features/Connect/features/ConnectATSIntegration/enums';

const YES: FinalRecommendationOption = { alignment: 'left', text: 'Yes' };
const MAYBE: FinalRecommendationOption = { alignment: 'middle', text: 'Maybe' };
const NO: FinalRecommendationOption = { alignment: 'right', text: 'No' };

export const allFinalRecommendationOptions = [YES, MAYBE, NO];
export const shortenedFinalRecommendationOptions = [YES, NO];

const getInitialAlignment = (defaultValue) => {
  if (!defaultValue) return null;

  const alignmentMap = {
    [CandidateFeedbackFinalRecommendation.YES]: 'left',
    [CandidateFeedbackFinalRecommendation.MAYBE]: 'middle',
    [CandidateFeedbackFinalRecommendation.NO]: 'right',
    [LongCandidateFeedbackFinalRecommendation.YES]: 'left',
    [LongCandidateFeedbackFinalRecommendation.MAYBE]: 'middle',
    [LongCandidateFeedbackFinalRecommendation.NO]: 'right',
  };

  return alignmentMap[defaultValue] || 'left';
};

export const FinalRecommendationQuestion: React.FC<FinalRecommendationQuestionProps> = ({
  dataTestId,
  isRequired,
  isReadOnly = false,
  onToggle,
  includeMaybeOption,
  questionText,
  defaultValue,
}) => {
  const [alignment, setAlignment] = useState<string | null>(getInitialAlignment(defaultValue));
  const theme = useTheme();

  const options = includeMaybeOption
    ? allFinalRecommendationOptions
    : shortenedFinalRecommendationOptions;

  const handleAlignment = (
    _event: React.MouseEvent<HTMLElement>,
    newAlignment: 'left' | 'middle' | 'right' | null
  ) => {
    setAlignment(newAlignment);
    if (newAlignment === 'left') {
      onToggle(FinalRecommendation.YES);
    } else if (newAlignment === 'middle') {
      onToggle(FinalRecommendation.MAYBE);
    } else if (newAlignment === 'right') {
      onToggle(FinalRecommendation.NO);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <QuestionLabel
        dataTestId={dataTestId}
        questionText={questionText}
        sx={{ paddingBottom: theme.spacing(0.5) }}
        isRequired={isRequired}
      />
      <ToggleButtonGroup color="primary" data-testid={dataTestId} exclusive value={alignment}>
        {options.map((option) => (
          <ToggleButton
            data-testid={`${dataTestId}-toggle-button-${option.alignment}`}
            key={option.alignment}
            value={option.alignment}
            onClick={handleAlignment}
            disabled={isReadOnly}
          >
            {option.text}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </Box>
  );
};
